import React, { useEffect, useState, useRef, useCallback } from "react";
import "react-datepicker/dist/react-datepicker.css";
import Modal from "../Modal/Modal";
import DatePicker, { registerLocale } from "react-datepicker";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import Breadcrumb from "../Breadcrumb";
import Config from '../../constants/Config';
import { logoutUser } from "../../actions/user";
import { subMonths, addDays, format, isBefore } from "date-fns";
import { ja } from "date-fns/locale";
import { getOperationDetail, getLoginStatus, normalizeContent, getFolderEdition } from "../../helpers/logHelpers";
import "./LogList.css";

// === ★ locale を登録（これがないと React が object を child として受け取りクラッシュする） ===
registerLocale("ja", ja);

const LogList = React.memo(({ 
  isFetching, 
  searchCount = 0,
  searchKeys = [], 
  logs_data, 
  fetchLogs, 
  fetchLogsCount, 
  user_info, 
  fetchUserInfo 
}) => {   

    if( !Config.RELEASE_MODE ){
      console.log("LogList Render");
    }
    
    const dispatch = useDispatch();
    const location = useLocation();

    /* -------------------------------
       検索条件（検索フォーム入力値）
       ------------------------------- */
    const [startDate, setStartDate] = useState(addDays(subMonths(new Date(), 1), 1));              // 開始日（初期値：1か月前）
    const [endDate, setEndDate] = useState(new Date());                                            // 終了日
    const [searchStartDate, setSearchStartDate] = useState(addDays(subMonths(new Date(), 1), 1));  // 開始日（初期値：1か月前）
    const [searchEndDate, setSearchEndDate] = useState(new Date());                                // 終了日
    const [loginId, setLoginId] = useState("");          // ログインID
    const [userName, setUserName] = useState("");        // ユーザー名
    const [title, setTitle] = useState("");              // タイトル
    const [autoNumber, setAutoNumber] = useState("");    // オートナンバー

    const safeRender = (value) => {
      if (value === null || value === undefined) return "";

      // object は直接表示しない
      if (typeof value === "object") {
        return ""; 
      }

      return value;
    };    

    /* -------------------------------
       モーダルエラーメッセージ
       ------------------------------- */
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState('');

    /* -------------------------------
       ページング関連
       ------------------------------- */
    const [currentPage, setCurrentPage] = useState(1);
    const [currentGroup, setCurrentGroup] = useState(0);
    const previousGroupRef = useRef(1);
    const itemsPerPage = 200;                                  // 1ページあたりの件数
    const buttonsPerGroup = 1;
    const totalPages = Math.ceil(searchCount / itemsPerPage);  // 全ページ数

    /* -------------------------------
       グループ内ページ移動
       ------------------------------- */
    const goToPage = async (page) => {
        const newGroup = Math.ceil(page / buttonsPerGroup);    
           
        if (newGroup !== previousGroupRef.current) {
              // ログデータを取得
              const apiStartDate = format(searchStartDate, 'yyyy-MM-dd');
              const apiEndDate = format(searchEndDate, 'yyyy-MM-dd');

              await fetchLogs(
                apiStartDate,
                apiEndDate,
                loginId, 
                userName, 
                title, 
                autoNumber, 
                searchKeys[newGroup-1]
              );  

            previousGroupRef.current = newGroup;
        }
    
        setCurrentPage(page);
        setCurrentGroup(newGroup);
        setIsModalOpen(false);
    };

    /* -------------------------------
       カレンダーの土日色付け
       ------------------------------- */
    const highlightWeekends = (date) => {
      const day = date.getDay();
      return day === 0 ? "highlight-sunday" : day === 6 ? "highlight-saturday" : "";
    };

    /* -------------------------------
       日付変更イベント
       ------------------------------- */
    const handleEndDateChange = (date) => setEndDate(date);
    const handleStartDateChange = (date) => setStartDate(date);

    /* -------------------------------
       表示（検索）ボタン押下処理
       入力チェック → API検索
       ------------------------------- */
    const handleSearch = async () => {
      // 入力チェック：日付必須
      if (!startDate || !endDate) {
          setModalMessage("期間を入力してください");
          setIsModalOpen(true);
          return;
      }

      // 入力チェック：終了日 < 開始日
      if (isBefore(endDate, startDate)) {
          setModalMessage("正しい期間を入力してください");
          setIsModalOpen(true);
          return;
      }

      // 1年以内制限
      const maxEndDate = addDays(subMonths(startDate, -12), -1);
      if (isBefore(maxEndDate, endDate)) {
          setModalMessage("検索期間は1年以内で入力してください"); 
          setIsModalOpen(true);
          return;
      }

      // 検索期間を保存
      setSearchStartDate(startDate);
      setSearchEndDate(endDate);
        
      try {          
          // APIコール（ログ検索）
          const apiStartDate = format(startDate, 'yyyy-MM-dd');
          const apiEndDate = format(endDate, 'yyyy-MM-dd');

          // データ件数を取得
          await fetchLogsCount(
            apiStartDate, 
            apiEndDate, 
            loginId, 
            userName, 
            title, 
            autoNumber
          );

          // ログデータを取得
          await fetchLogs(
            apiStartDate, 
            apiEndDate, 
            loginId,
            userName, 
            title, 
            autoNumber, 
            searchKeys[0]
          );

          setCurrentGroup(1); // グループ位置リセット
          setCurrentPage(1);  // ページ位置リセット
          previousGroupRef.current = 1; // 前回グループ位置リセット
      } catch (error) {
          setModalMessage(error.message || "Failed to search.");
          setIsModalOpen(true);
      }
    };
    
    /* -------------------------------
       初期表示
       ------------------------------- */
    useEffect(() => {
      const fetchData = async () => {
          try {
              // ログインユーザー情報取得    
              await fetchUserInfo();  
              
              const apiStartDate = format(startDate, 'yyyy-MM-dd');
              const apiEndDate = format(endDate, 'yyyy-MM-dd');
              
              // データ件数を取得
              await fetchLogsCount(
                apiStartDate, 
                apiEndDate, 
                loginId, 
                userName, 
                title, 
                autoNumber
              );    

              // ログデータを取得
              await fetchLogs(
                apiStartDate, 
                apiEndDate, 
                loginId, 
                userName, 
                title, 
                autoNumber, 
                searchKeys[0]
              );  
          } finally {
              setCurrentGroup(1);
              setCurrentPage(1);
          }
      };
      fetchData();
    }, [fetchUserInfo, fetchLogs, location.state?.reloadAt]);

    /* -------------------------------
       ページング用データ分割
       ------------------------------- */
    console.log("LogList- currentGroup:", currentGroup);
    console.log("LogList- currentPage:", currentPage);
    console.log("LogList- logs_data:", logs_data);
    const startIndex = ((currentPage - 1) % buttonsPerGroup) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = logs_data.slice(startIndex, endIndex);  

    console.log("LogList- paginatedData:", paginatedData);


    const safeFormatDate = (date) => {
      try {
        return format(new Date(date), "yyyy/MM/dd HH:mm:ss");
      } catch {
        return "";
      }
    };

    /* -------------------------------
      カレンダーカスタムヘッダー（共通）
      ------------------------------- */
    const renderCalendarHeader = ({
      date,
      changeYear,
      changeMonth,
      decreaseMonth,
      increaseMonth,
    }) => {
      const currentYear = new Date().getFullYear();
      const years = Array.from({ length: 30 }, (_, i) => currentYear - i);

      const months = [
        "01","02","03","04","05","06",
        "07","08","09","10","11","12"
      ];

      return (
        <div
          className="custom-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 8px",
          }}
        >
          {/* Prev button */}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              decreaseMonth();
            }}
          >
            {"<"}
          </button>

          {/* Year + Month */}
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <div>
              <select
                className="year-select"
                value={date.getFullYear()}
                onChange={(e) => changeYear(Number(e.target.value))}
                style={{ padding: "2px 4px" }}
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <span>年</span>
            </div>

            <div>
              <select
                className="month-select"
                value={date.getMonth()}
                onChange={(e) => changeMonth(Number(e.target.value))}
                style={{ padding: "2px 4px" }}
              >
                {months.map((m, i) => (
                  <option key={i} value={i}>{m}</option>
                ))}
              </select>
              <span>月</span>
            </div>
          </div>

          {/* Next button */}
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              increaseMonth();
            }}
          >
            {">"}
          </button>
        </div>
      );
    };

    // ============================================================================
    //  詳細内容の表示
    // ============================================================================
    const renderContentDetail = useCallback(
      (item) => {
        // content を整形（value="" の項目を削除）
        const cleaned = normalizeContent(item.content);

        // ★ cleaned が空オブジェクトなら何も表示しない
        if (!cleaned || Object.keys(cleaned).length === 0) {
          return null; // ← 空白セルにする
        }

        // JSON 文字列に整形（pretty-print）
        const jsonText = JSON.stringify(cleaned, null, 2);

        return (
          <pre className="json-pre">
            {jsonText}
          </pre>
        );
      },
      []
    );


    /* -------------------------------
       操作内容＋ログインステータス整形（画面表示用）
       ------------------------------- */
    const renderOperationDetail = useCallback(
      (item) => {
          const operation_detail = getOperationDetail(item.log_timing);
          const login_status = getLoginStatus(item.log_status);

          // ログイン時のみ 「操作内容（状態）」と表示
          if (operation_detail?.id === 1) {
            return (
              <span style={{ color: login_status?.hex || operation_detail?.hex || "#000" }}>
                {operation_detail?.text || "不明"}（{login_status?.text || "未知"}）
              </span>
            );
          }

          // その他は操作内容のみ表示
          return (
            <span style={{ color: operation_detail?.hex || "#000" }}>
              {operation_detail?.text || "不明"}
            </span>
          );
      },
      [getOperationDetail, getLoginStatus]
    );

    /* -------------------------------
      オートナンバー表示
      folder_edition-auto_number（例：TK-100079）
       ------------------------------- */
    const renderAutoNumber = useCallback(
      (item) => {
        // auto_number がない場合は表示しない
        if (!item?.auto_number) return "";

        if (typeof item.auto_number === "object") return "";

        // folder_id は content 内にある
        const folderId = item?.content?.folder_id;

        // folder_id から版数を取得
        const edition = getFolderEdition(folderId);

        // 版数が取得できない場合は番号のみ表示
        if (!edition) {
          return item.auto_number;
        }

        return `${edition}-${item.auto_number}`;
      },
      []
    );

    // ================================
    // ページネーション
    // ================================
    const renderPagination = useCallback(() => {
      const maxButtons = 10;
      const total = Math.max(totalPages || 1, 1);    // totalPages が 0 / undefined の場合を防止

      // currentPage が範囲外の場合に安全な値へ補正
      const safePage = Math.min(Math.max(currentPage, 1), total);

      // ブロックページングの計算
      const currentBlock = Math.floor((safePage - 1) / maxButtons);
      const start = currentBlock * maxButtons + 1;
      const end = Math.min(start + maxButtons - 1, total);

      const buttons = [];
      for (let p = start; p <= end; p++) {
        buttons.push(
          <button
            key={`page_${p}`}
            className={`page-number-btn ${p === safePage ? "active" : ""}`}
            onClick={() => goToPage(p)}
          >
            {p}
          </button>
        );
      }

      return buttons.length ? <>{buttons}</> : null;
    }, [currentPage, totalPages]);

    return (
      <div id="Main">
        <div id="Contents">

          {/* パンくず＋ログアウト */}
          <Breadcrumb
            user={user_info?.admin_name}
            onLogout={() => dispatch(logoutUser())}
          />

          {/* エラーモーダル */}
          {isModalOpen && (
            <Modal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              message={modalMessage}
            />
          )}

          <div id="logs-layout">
            <div className="logs-main">
              <h2 className="logs-title">ログ一覧</h2>

              {/* ===== 検索フォーム ===== */}
              <form className="logs-search-form">

                {/* 期間（開始〜終了） */}
                <div className="logs-search-row">
                  <div className="logs-label">日付</div>

                  {/* 開始日 */}
                  <DatePicker
                    selected={startDate}
                    onChange={handleStartDateChange}
                    dayClassName={(date) => {
                        const weekendClass = highlightWeekends(date);
                        const isCurrentMonth = startDate && date ? date.getMonth() === startDate.getMonth() : true;
                        return isCurrentMonth ? weekendClass : "hide-day";
                    }}
                    locale="ja"
                    dateFormat="yyyy/MM/dd"
                    className="custom-datepicker date-input"
                    calendarStartDay={1}
                    renderCustomHeader={renderCalendarHeader}   // 共通関数
                    onKeyDown={(e) => e.preventDefault()}       // キーボード入力禁止                                        
                  />

                  <span className="logs-range">〜</span>

                  {/* 終了日 */}
                  <DatePicker
                    selected={endDate}
                    onChange={handleEndDateChange}
                    dayClassName={(date) => {
                        const weekendClass = highlightWeekends(date);
                        const isCurrentMonth = endDate && date ? date.getMonth() === endDate.getMonth() : true;                  
                        return isCurrentMonth ? weekendClass : "hide-day";
                    }}
                    locale="ja"
                    dateFormat="yyyy/MM/dd"
                    className="custom-datepicker date-input"
                    calendarStartDay={1}
                    renderCustomHeader={renderCalendarHeader}   // 共通関数
                    onKeyDown={(e) => e.preventDefault()}       // キーボード入力禁止                                                            
                  />
                </div>

                {/* ログインID・ユーザー名・タイトル・オートナンバー */}
                <div className="logs-search-row">
                  <div className="logs-label">ログインID</div>
                  <input
                    type="text"
                    className="logs-input"
                    value={loginId}
                    onChange={(e) => setLoginId(e.target.value)}
                  />

                  <div className="logs-label">ユーザー名</div>
                  <input
                    type="text"
                    className="logs-input"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                  />

                  <div className="logs-label">タイトル</div>
                  <input
                    type="text"
                    className="logs-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />

                  <div className="logs-label">オートナンバー</div>
                  <input
                    type="text"
                    className="logs-input"
                    value={autoNumber}
                    onChange={(e) => setAutoNumber(e.target.value)}
                  />

                  {/* 表示ボタン */}
                  <button
                    type="button"
                    className="logs-search-btn"
                    onClick={handleSearch}
                  >
                    表示
                  </button>
                </div>
              </form>

              {/* ===== 一覧テーブル ===== */}
              <div className="logs-table-wrapper">                
                <table className="logs-table">
                  <thead>
                    <tr>
                      <th className="colDate">日付</th>
                      <th className="colLoginId">ログインID</th>
                      <th className="colUserName">ユーザー名</th>
                      <th className="colFolder">フォルダー</th>
                      <th className="colTitle">タイトル</th>
                      <th className="colContent">詳細内容</th>
                      <th className="colOperation">操作内容</th>
                      <th className="colNumber">ｵｰﾄﾅﾝﾊﾞｰ</th>    
                    </tr>
                  </thead>

                  <tbody>
                    {/* ===============================
                      データが0件の場合の表示
                      =============================== */}
                    {!isFetching && paginatedData.length === 0 && (
                      <tr>
                        <td
                          colSpan={8}              // テーブルの列数分結合
                          className="no-data-cell" // データなし用スタイル
                        >
                          データがありません。
                        </td>
                      </tr>
                    )}

                    {!isFetching && paginatedData.map((item, index) => (
                      <tr key={item.admin_id || index}>
                        <td>{safeFormatDate(item.log_datetime)}</td>
                        <td>{safeRender(item.login_id)}</td>
                        <td>{safeRender(item.user_name)}</td>
                        <td>{safeRender(item.folder_name)}</td>
                        <td>{safeRender(item.title)}</td>
                        <td className="json-cell">{renderContentDetail(item)}</td>
                        <td>{renderOperationDetail(item)}</td>
                        <td>{renderAutoNumber(item)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ===== 件数 + ページング ===== */}
            <div className="logs-footer-wrapper">
              <span className="logs-count">件数：{searchCount ?? 0}</span>

              {/* 件数が1件以上の場合のみページングを表示 */}
              {searchCount > 0 && (
                <div className="pagination-center">
                  <button
                    className="page-btn"
                    disabled={currentPage === 1}
                    onClick={() => goToPage(1)}
                  >
                    ≪
                  </button>

                  <button
                    className="page-btn"
                    disabled={currentPage === 1}
                    onClick={() => goToPage(currentPage - 1)}
                  >
                    ＜
                  </button>

                  {renderPagination()}

                  <button
                    className="page-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => goToPage(currentPage + 1)}
                  >
                    ＞
                  </button>

                  <button
                    className="page-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => goToPage(totalPages)}
                  >
                    ≫
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ローディングスピナー */}
        <div className={`overlay ${isFetching ? 'active' : ''}`}>
          <div className={`spinner ${isFetching ? 'active' : ''}`}></div>
        </div>
      </div>
    );
  }
);

LogList.displayName = "LogList";

LogList.propTypes = {
  isFetching: PropTypes.bool.isRequired,
  user_info: PropTypes.object,
  searchCount: PropTypes.number,
  searchKeys: PropTypes.array,
  logs_data: PropTypes.array,
  error: PropTypes.any,
  fetchLogsCount: PropTypes.func.isRequired,
  fetchLogs: PropTypes.func.isRequired,
  fetchUserInfo: PropTypes.func.isRequired,
};

export default LogList;